import React, { FC } from 'react';
import NumberFormat, { NumberFormatProps, NumberFormatValues } from 'react-number-format';
import { Message } from 'react-hook-form';
import clsx from 'clsx';

import InputError from 'components/InputError';
import useLayoutTheme from 'hooks/useLayoutTheme';
import { ReactComponent as WarningIcon } from 'images/warning-icon.svg';

import styles from './NumberInput.module.scss';

const DECIMAL_PLACES_AMOUNT = 0;

export enum NumberInputHintVariant {
  Default = 'default',
  Warning = 'warning',
}

interface NumberInputProps extends NumberFormatProps {
  label: string;
  errorMessage?: Message | undefined;
  decimalPlacesAmount?: number;
  disabled?: boolean;
  value?: string;
  isAllowed?: (values: NumberFormatValues) => boolean;
  format?: string;
  dataNeuroLabel?: string;
  hint?: string;
  hintVariant?: NumberInputHintVariant;
}

const NumberInput: FC<NumberInputProps> = ({
  prefix,
  label,
  onChange,
  placeholder,
  className,
  allowNegative,
  allowLeadingZeros,
  thousandSeparator,
  suffix,
  errorMessage,
  onBlur,
  name,
  maxLength,
  max,
  disabled,
  value,
  isAllowed,
  format,
  decimalPlacesAmount = DECIMAL_PLACES_AMOUNT,
  dataNeuroLabel,
  hint,
  hintVariant = NumberInputHintVariant.Default,
  ...props
}) => {
  const { themeStyles } = useLayoutTheme();
  return (
    <div className={className}>
      <div className={clsx(styles.inputContainer)}>
        {label && <p className={clsx(styles.label, themeStyles.inputLabel)}>{label}</p>}
        <NumberFormat
          type="text"
          className={clsx(styles.input, themeStyles.input, !!errorMessage && styles.error)}
          prefix={prefix}
          suffix={suffix}
          onChange={onChange}
          placeholder={placeholder}
          allowNegative={allowNegative}
          allowLeadingZeros={allowLeadingZeros}
          thousandSeparator={thousandSeparator}
          decimalScale={decimalPlacesAmount}
          onBlur={onBlur}
          name={name}
          maxLength={maxLength}
          max={max}
          disabled={disabled}
          value={value}
          isAllowed={isAllowed}
          format={format}
          data-neuro-label={dataNeuroLabel}
          {...props}
        />
      </div>
      {!!hint && (
        <div className={clsx(styles.hint, hintVariant === NumberInputHintVariant.Warning && styles.hintWarning)}>
          {hintVariant === NumberInputHintVariant.Warning && <WarningIcon className={styles.hintIcon} />}
          <span>{hint}</span>
        </div>
      )}
      {!!errorMessage && <InputError errorMessage={errorMessage} />}
    </div>
  );
};

export default NumberInput;
